import { createRouter, createWebHistory } from 'vue-router'
import i18n from '../i18n'
import Home from '../views/Home.vue'
import Zones from '../views/Zones.vue'
import Login from '../views/auth/Login.vue'
import Register from '../views/auth/Register.vue'
import ForgotPassword from '../views/auth/ForgotPassword.vue'
import ResetPassword from '../views/auth/ResetPassword.vue'
import OAuthCallback from '../views/auth/OAuthCallback.vue'
import CompleteUsername from '../views/auth/CompleteUsername.vue'
import ConfirmEmail from '../views/auth/ConfirmEmail.vue'

// Route definitions with per-locale paths.
// Routes with a single `path` string use the same path for both locales.
const routeDefs = [
  { name: 'Home', paths: { ro: '/', en: '/en' }, component: Home },
  { name: 'Zones', paths: { ro: '/zone', en: '/en/zones' }, component: Zones },
  {
    name: 'Trail',
    paths: { ro: '/:zone_slug/trasee/:slug', en: '/en/:zone_slug/trails/:slug' },
    component: () => import('../views/Trail.vue'),
  },
  {
    name: 'POI',
    paths: { ro: '/:zone_slug/poi/:slug', en: '/en/:zone_slug/pois/:slug' },
    component: () => import('../views/POI.vue'),
  },
  {
    name: 'About',
    paths: { ro: '/despre-noi', en: '/en/about' },
    component: () => import('../views/AboutUs.vue'),
  },
  {
    name: 'Privacy',
    paths: { ro: '/politica-de-confidentialitate', en: '/en/privacy-policy' },
    component: () => import('../views/Privacy.vue'),
  },
  {
    name: 'Terms',
    paths: { ro: '/termeni-si-conditii', en: '/en/terms-of-service' },
    component: () => import('../views/Terms.vue'),
  },
  {
    name: 'UserProfile',
    paths: { ro: '/u/:username', en: '/en/u/:username' },
    component: () => import('../views/UserProfile.vue'),
  },
  {
    name: 'UserTrails',
    paths: { ro: '/u/:username/trasee', en: '/en/u/:username/trails' },
    component: () => import('../views/UserTrails.vue'),
  },
  {
    name: 'UserComments',
    paths: { ro: '/u/:username/comentarii', en: '/en/u/:username/comments' },
    component: () => import('../views/UserComments.vue'),
  },
  {
    name: 'UserActivities',
    paths: { ro: '/u/:username/activitati', en: '/en/u/:username/activities' },
    component: () => import('../views/UserActivities.vue'),
  },
  {
    name: 'PublicActivity',
    paths: { ro: '/u/:username/activitati/:slug', en: '/en/u/:username/activities/:slug' },
    component: () => import('../views/PublicActivityShow.vue'),
  },
  {
    name: 'Zone',
    paths: { ro: '/:slug', en: '/en/:slug' },
    component: () => import('../views/Zone.vue'),
  },
  {
    name: 'ZoneTrails',
    paths: { ro: '/:slug/trasee', en: '/en/:slug/trails' },
    component: () => import('../views/ZoneTrails.vue'),
  },
  {
    name: 'ZonePois',
    paths: { ro: '/:slug/poi', en: '/en/:slug/pois' },
    component: () => import('../views/ZonePois.vue'),
  },
  { name: 'Login', paths: { ro: '/autentificare', en: '/en/login' }, component: Login },
  { name: 'Register', paths: { ro: '/inregistrare', en: '/en/register' }, component: Register },
  { name: 'ForgotPassword', path: '/forgot-password', component: ForgotPassword },
  { name: 'ResetPassword', path: '/reset-password', component: ResetPassword },
  { name: 'OAuthCallback', path: '/auth/oauth/callback', component: OAuthCallback },
  {
    name: 'CompleteUsername',
    path: '/complete-username',
    component: CompleteUsername,
    meta: { requiresAuth: true },
  },
  { name: 'ConfirmEmail', path: '/confirm-email', component: ConfirmEmail },
]

// Dashboard routes use identical English paths for both locales (behind auth, not SEO-relevant).
const dashboardRoute = {
  path: '/dashboard',
  component: () => import('../views/Dashboard.vue'),
  meta: { requiresAuth: true },
  children: [
    {
      path: '',
      name: 'Dashboard',
      component: () => import('../views/dashboard/DashboardOverview.vue'),
    },
    {
      path: 'trails',
      name: 'MyTrails',
      component: () => import('../views/dashboard/MyTrails.vue'),
    },
    {
      path: 'activities',
      name: 'MyActivities',
      component: () => import('../views/dashboard/MyActivities.vue'),
    },
    {
      path: 'comments',
      name: 'MyComments',
      component: () => import('../views/dashboard/MyComments.vue'),
    },
  ],
}

const standaloneDashboardRoutes = [
  {
    path: '/dashboard/profile',
    name: 'Profile',
    component: () => import('../views/dashboard/Profile.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/activities/new',
    name: 'ActivityNew',
    component: () => import('../views/dashboard/ActivityNew.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/activities/:slug',
    name: 'ActivityShow',
    component: () => import('../views/dashboard/ActivityShow.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/trails/new/:activitySlug',
    name: 'TrailCreate',
    component: () => import('../views/dashboard/TrailCreate.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/trails/:zone_slug/:slug/edit',
    name: 'TrailEdit',
    component: () => import('../views/dashboard/TrailEdit.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/dashboard/trails/:zone_slug/:slug/edit-points',
    redirect: (to) => `/dashboard/trails/${to.params.zone_slug}/${to.params.slug}/edit`,
  },
]

const debugRoutes = [
  { path: '/debug/icons', name: 'IconDebug', component: () => import('../views/IconDebug.vue') },
]

// Generate routes from routeDefs. Each entry with `paths` produces two routes (ro + en).
// Entries with a single `path` produce one shared route.
function generateRoutes(defs) {
  const routes = []
  for (const def of defs) {
    const { name, paths, path, component, meta } = def
    if (paths) {
      routes.push(
        {
          path: paths.ro,
          name,
          component,
          meta: { ...meta, locale: 'ro', routeKey: name },
        },
        {
          path: paths.en,
          name: `${name}.en`,
          component,
          meta: { ...meta, locale: 'en', routeKey: name },
        },
      )
    } else {
      // Shared route (same path for both locales) — no routeKey so LanguageSwitcher falls back to home
      routes.push({ path, name, component, meta })
    }
  }
  return routes
}

const notFoundRoutes = [
  {
    path: '/en/:pathMatch(.*)*',
    name: 'NotFound.en',
    component: () => import('../views/NotFound.vue'),
    meta: { locale: 'en', routeKey: 'NotFound' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue'),
    meta: { locale: 'ro', routeKey: 'NotFound' },
  },
]

const routes = [
  ...generateRoutes(routeDefs),
  dashboardRoute,
  ...standaloneDashboardRoutes,
  ...debugRoutes,
  ...notFoundRoutes,
]

// Auth route names that skip the auth guard
import { isAuthRouteName } from './authRoutes'
import { installChunkReloadGuards } from './chunkReload'

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from) {
    // Only treat the hash as an element selector if it looks like a valid CSS ID anchor.
    // OAuth/token hashes (e.g. #token=abc123) contain '=' and other invalid chars — skip them.
    if (to.hash && /^#[a-zA-Z][a-zA-Z0-9_-]*$/.test(to.hash)) {
      return { el: to.hash, behavior: 'smooth' }
    }
    if (to.path !== from.path) return { top: 0 }
  },
})

// Resolve the locale-specific route name for a given base name.
// E.g. resolveLocaleName('Zone', 'en') -> 'Zone.en'
//      resolveLocaleName('Zone', 'ro') -> 'Zone'
export function resolveLocaleName(routeKey, locale) {
  return locale === 'en' ? `${routeKey}.en` : routeKey
}

// eslint-disable-next-line unicorn/no-top-level-side-effects -- registering the navigation guard is this module's purpose
router.beforeEach(async (to) => {
  const routeLocale = to.meta?.locale
  if (routeLocale) {
    i18n.global.locale.value = routeLocale
    document.documentElement.lang = routeLocale
    localStorage.setItem('locale', routeLocale)
  }

  // Shared routes (forgot-password, etc.) don't set locale from meta.
  // Keep the current locale as-is.

  const routeName = to.name?.toString() || ''
  if (isAuthRouteName(routeName)) return true

  const token = localStorage.getItem('token')

  if (to.meta?.requiresAuth && !token) {
    const locale = i18n.global.locale.value
    return { name: resolveLocaleName('Login', locale), query: { redirect: to.fullPath } }
  }

  if (!token) return true

  const { useAuthStore } = await import('../stores/auth')
  const authStore = useAuthStore()

  if (!authStore.user) {
    try {
      await authStore.fetchUser()
    } catch {
      const { handleSessionExpired } = await import('../api/client')
      const { target } = await handleSessionExpired(to.fullPath)
      if (to.meta?.requiresAuth) {
        return target
      }
      return true
    }
  }

  if (!authStore.hasUsername && to.name !== 'CompleteUsername') {
    return { name: 'CompleteUsername' }
  }

  if (to.name === 'CompleteUsername' && authStore.hasUsername) {
    return { path: '/' }
  }

  return true
})

// Recover from stale Vite chunks after a deploy (one-time reload to the target).
// eslint-disable-next-line unicorn/no-top-level-side-effects -- installing the router's chunk-reload guards is this module's purpose
installChunkReloadGuards(router)

export default router
